import { Injectable } from '@angular/core';
import { Observable, forkJoin } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { LanguageChangeEvent } from 'src/app/adapters/translation/model/language-change.model';
import { Translations } from 'src/app/adapters/translation/model/translations.model';
import { TranslationAdapter } from 'src/app/adapters/translation/translation.adapter';
import { LanguageNotSupportedError } from 'src/app/models/error.model';

@Injectable({
  providedIn: 'root'
})
export class TranslationUseCase {
  constructor(
    private translationAdatper: TranslationAdapter,
  ) {}

  setLanguage(language: string): Observable<void> {
    const obs = this.translationAdatper.isLanguageSupported(language).pipe(
      switchMap((isSupported, _) => {
        if (isSupported) {
          return this.translationAdatper.setLanguage(language);
        } else {
          const error = new LanguageNotSupportedError();
          throw error;
        }
      })
    );

    return obs;
  }

  setDefaultLanguage(language: string): Observable<void> {
    const obs = this.translationAdatper.isLanguageSupported(language).pipe(
      switchMap((isSupported, _) => {
        if (isSupported) {
          return this.translationAdatper.setDefaultLanguage(language);
        } else {
          const error = new LanguageNotSupportedError();
          throw error;
        }
      })
    );

    return obs;
  }

  getBrowserLanguage(): Observable<string> {
    const allLanguagesObs = forkJoin([
      this.translationAdatper.getCurrentCulturalLanguage(),
      this.translationAdatper.getBrowserCurrentLanguage(),
      this.translationAdatper.getDefaultLanguage()
    ]).pipe(
      switchMap(([culturalLanguage, localeLanguage, defaultLanguage]) =>
        this.getMostSupportedLanguage(culturalLanguage, localeLanguage, defaultLanguage)
      )
    );

    return allLanguagesObs;
  }

  getCurrentLanguage(): Observable<string> {
    const languageObs = this.translationAdatper.getCurrentLangauge();
    return languageObs;
  }

  getLanguageChangeStream(): Observable<LanguageChangeEvent> {
    const stream = this.translationAdatper.getLanguageChangeStream();
    return stream;
  }

  getCurrentLanguageTranslations(): Observable<Translations> {
    const translationsObs = this.translationAdatper.getCurrentLangauge().pipe(
      switchMap((langauge) => {
        const translationsObs = this.translationAdatper.getTranslationsForLanguage(langauge);
        return translationsObs;
      })
    );

    return translationsObs;
  }

  private getMostSupportedLanguage(culturalLanguage, localeLanguage, defaultLanguage): Observable<string> {
    const mostSupportedLanguageObs = forkJoin([
      this.translationAdatper.isLanguageSupported(culturalLanguage),
      this.translationAdatper.isLanguageSupported(localeLanguage)
    ]).pipe(
      map(([isCulturalLangSupported, isLocaleLangSupported]) => {
        if (isCulturalLangSupported) {
          return culturalLanguage;
        } else if (isLocaleLangSupported) {
          return localeLanguage;
        } else {
          return defaultLanguage;
        }
      })
    );

    return mostSupportedLanguageObs;
  }
}
